import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';

@Component({
    selector: 'ca-external-login-settings',
    templateUrl: './external-login-settings.component.html',
    styleUrls: ['./external-login-settings.component.scss'],
    standalone: false
})
export class ExternalLoginSettingsComponent implements OnInit {
  externalLoginSettingsForm: FormGroup;
  externalLoginSettingDtos: ConfigurationSettingsDto[] = [];

  private readonly settingKeySamlLoginEndPoint = 'ExternalLogin.SamlLoginEndPoint';
  private readonly settingKeySamlLoginEnabled = 'ExternalLogin.SamlLoginEnabled';
  private readonly settingKeySamlDefaultRedirectEndPoint =
    'ExternalLogin.SamlDefaultRedirectEndPoint';
  private readonly settingKeySilentLoginEnabled = 'ExternalLogin.SilentLoginEnabled';
  private readonly settingKeySilentLoginMetadataUrl = 'ExternalLogin.SilentLoginMetadataUrl';
  private readonly settingKeySilentLoginValidIssuers = 'ExternalLogin.SilentLoginValidIssuers';
  private readonly settingKeySilentLoginValidAudiences = 'ExternalLogin.SilentLoginValidAudiences';
  private readonly settingKeySilentLoginUserIdentifierClaim =
    'ExternalLogin.SilentLoginUserIdentifierClaim';
  private readonly settingKeySSOHandoffAllowedOrigins = 'SSOHandoff.AllowedOrigins';
  private readonly settingKeyAuthCodeFlowSilentLoginEnabled =
    'ExternalLogin.AuthCodeFlowSilentLoginEnabled';
  private readonly settingKeyAuthCodeFlowSilentLoginAuthority =
    'ExternalLogin.AuthCodeFlowSilentLoginAuthority';
  private readonly settingKeyAuthCodeFlowSilentLoginClientId =
    'ExternalLogin.AuthCodeFlowSilentLoginClientId';
  private readonly settingKeyAuthCodeFlowSilentLoginClientSecret =
    'ExternalLogin.AuthCodeFlowSilentLoginClientSecret';

  acceptFileExtensions: string[] = ['.cert'];
  selectedCertFile: File | null = null;

  constructor(
    private config: ConfigStateService,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private fb: FormBuilder,
    private localizationService: LocalizationService
  ) {
    this.externalLoginSettingsForm = fb.group({
      samlLoginEndPoint: [this.config.getSetting(this.settingKeySamlLoginEndPoint)],
      samlLoginEnabled: [
        this.config.getSetting(this.settingKeySamlLoginEnabled) === 'true' ? true : false,
      ],
      samlDefaultRedirectEndPoint: [
        this.config.getSetting(this.settingKeySamlDefaultRedirectEndPoint),
      ],
      silentLoginEnabled: [
        this.config.getSetting(this.settingKeySilentLoginEnabled) === 'true' ? true : false,
      ],
      silentLoginMetadataUrl: [this.config.getSetting(this.settingKeySilentLoginMetadataUrl)],
      silentLoginValidIssuers: [this.config.getSetting(this.settingKeySilentLoginValidIssuers)],
      silentLoginValidAudiences: [
        this.config.getSetting(this.settingKeySilentLoginValidAudiences),
      ],
      silentLoginUserIdentifierClaim: [
        this.config.getSetting(this.settingKeySilentLoginUserIdentifierClaim),
      ],
      ssoHandoffAllowedOrigins: [this.config.getSetting(this.settingKeySSOHandoffAllowedOrigins)],
      authCodeFlowSilentLoginEnabled: [
        this.config.getSetting(this.settingKeyAuthCodeFlowSilentLoginEnabled) === 'true'
          ? true
          : false,
      ],
      authCodeFlowSilentLoginAuthority: [
        this.config.getSetting(this.settingKeyAuthCodeFlowSilentLoginAuthority),
      ],
      authCodeFlowSilentLoginClientId: [
        this.config.getSetting(this.settingKeyAuthCodeFlowSilentLoginClientId),
      ],
      authCodeFlowSilentLoginClientSecret: [
        this.config.getSetting(this.settingKeyAuthCodeFlowSilentLoginClientSecret),
      ],
    });

    this.toggleSilentLoginControls(
      this.externalLoginSettingsForm.get('silentLoginEnabled').value,
      [
        'silentLoginMetadataUrl',
        'silentLoginValidIssuers',
        'silentLoginValidAudiences',
        'silentLoginUserIdentifierClaim',
      ]
    );
    this.toggleSilentLoginControls(
      this.externalLoginSettingsForm.get('authCodeFlowSilentLoginEnabled').value,
      [
        'authCodeFlowSilentLoginAuthority',
        'authCodeFlowSilentLoginClientId',
        'authCodeFlowSilentLoginClientSecret',
      ]
    );
  }

  ngOnInit(): void {
    this.externalLoginSettingsForm
      .get('silentLoginEnabled')
      .valueChanges.subscribe(value =>
        this.toggleSilentLoginControls(value, [
          'silentLoginMetadataUrl',
          'silentLoginValidIssuers',
          'silentLoginValidAudiences',
          'silentLoginUserIdentifierClaim',
        ])
      );

    this.externalLoginSettingsForm
      .get('authCodeFlowSilentLoginEnabled')
      .valueChanges.subscribe(value =>
        this.toggleSilentLoginControls(value, [
          'authCodeFlowSilentLoginAuthority',
          'authCodeFlowSilentLoginClientId',
          'authCodeFlowSilentLoginClientSecret',
        ])
      );
  }

  saveExternalLoginSettings() {
    this.externalLoginSettingDtos = [];

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeySamlLoginEndPoint,
      settingValue: this.externalLoginSettingsForm.get('samlLoginEndPoint').value.toString(),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeySamlLoginEnabled,
      settingValue: this.externalLoginSettingsForm.get('samlLoginEnabled').value.toString(),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeySamlDefaultRedirectEndPoint,
      settingValue: this.externalLoginSettingsForm
        .get('samlDefaultRedirectEndPoint')
        .value.toString(),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeySilentLoginEnabled,
      settingValue: this.getSettingValue('silentLoginEnabled'),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeySilentLoginMetadataUrl,
      settingValue: this.getSettingValue('silentLoginMetadataUrl'),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeySilentLoginValidIssuers,
      settingValue: this.getSettingValue('silentLoginValidIssuers'),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeySilentLoginValidAudiences,
      settingValue: this.getSettingValue('silentLoginValidAudiences'),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeySilentLoginUserIdentifierClaim,
      settingValue: this.getSettingValue('silentLoginUserIdentifierClaim'),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeySSOHandoffAllowedOrigins,
      settingValue: this.getSettingValue('ssoHandoffAllowedOrigins'),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeyAuthCodeFlowSilentLoginEnabled,
      settingValue: this.getSettingValue('authCodeFlowSilentLoginEnabled'),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeyAuthCodeFlowSilentLoginAuthority,
      settingValue: this.getSettingValue('authCodeFlowSilentLoginAuthority'),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeyAuthCodeFlowSilentLoginClientId,
      settingValue: this.getSettingValue('authCodeFlowSilentLoginClientId'),
    });

    this.externalLoginSettingDtos.push({
      settingName: this.settingKeyAuthCodeFlowSilentLoginClientSecret,
      settingValue: this.getSettingValue('authCodeFlowSilentLoginClientSecret'),
    });

    this.settingService.saveSetting(this.externalLoginSettingDtos).subscribe({
      next: () => {
        this.toastr.success(
          this.localizationService.instant('AbpSettingManagement::SavedSuccessfully')
        );
        if (this.selectedCertFile) {
          this.settingService.uploadSamlCertificate(this.selectedCertFile).subscribe({
            next: () => {
              setTimeout(() => {
                this.toastr.success(
                  this.localizationService.instant('Settings::CertificateImported')
                );
              }, 100);

              this.selectedCertFile = null;
            },
            error: () => {
              setTimeout(() => {
                this.toastr.error(
                  this.localizationService.instant('Settings::CertificateImportFailed')
                );
              }, 100);
            },
          });
        }
      },
    });
  }

  onImportFormClick(e: MouseEvent) {
    const el = document.getElementById('samlCertSelector') as HTMLElement;
    el.click();
  }

  onImportForm(files: FileList) {
    if (!files || files.length === 0) {
      return;
    }

    const file = files.item(0);
    if (!file) {
      return;
    }

    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!this.acceptFileExtensions.includes(extension)) {
      this.toastr.error(this.localizationService.instant('Settings::InvalidFormFile'));
      return;
    }

    this.selectedCertFile = file;
  }

  private toggleSilentLoginControls(enabled: boolean, controlNames: string[] = []) {
    const action = enabled ? 'enable' : 'disable';

    controlNames.forEach(controlName =>
      this.externalLoginSettingsForm.get(controlName)[action]({ emitEvent: false })
    );
  }

  private getSettingValue(controlName: string): string {
    const value = this.externalLoginSettingsForm.get(controlName).value;

    return value == null ? '' : value.toString();
  }
}
